import { lazy } from 'react';

export const CHUNK_RELOAD_FLAG = 'chunk-reload';

/**
 * Wraps a dynamic import with recovery for the one failure mode code-splitting
 * introduces in production: a browser holding a stale index.html requests chunk
 * filenames that no longer exist after a redeploy, and the import rejects.
 *
 * Policy:
 *  - success  -> clear the guard, so a later stale deploy in the same session
 *                can still self-heal rather than going straight to the crash screen.
 *  - offline  -> rethrow. A dropped connection is not a stale deploy; reloading
 *                would hand the user the service worker's offline page and lose
 *                their place. NetworkGate already swaps in NoInternetScreen and
 *                remounts on reconnect.
 *  - online   -> reload once (guarded), then rethrow to ErrorBoundary, which
 *                renders a message with its own Reload button.
 *
 * Dependencies are injected so the policy is testable without a real browser.
 */
export const createChunkLoader = (factory, {
  storage = (() => { try { return window.sessionStorage; } catch { return null; } })(),
  isOnline = () => (typeof navigator === 'undefined' ? true : navigator.onLine !== false),
  reload = () => window.location.reload(),
} = {}) => () =>
  factory()
    .then((mod) => {
      try { storage?.removeItem(CHUNK_RELOAD_FLAG); } catch { /* storage blocked */ }
      return mod;
    })
    .catch((err) => {
      if (!isOnline()) throw err;

      // No storage at all (sessionStorage access threw at init) means no loop
      // guard: optional chaining would silently no-op, leaving alreadyRetried
      // false forever and reloading on every attempt. Same rule as blocked
      // storage below — surface the error rather than risk a reload cycle.
      if (!storage) throw err;

      let alreadyRetried;
      try {
        alreadyRetried = storage.getItem(CHUNK_RELOAD_FLAG) === '1';
        if (!alreadyRetried) storage.setItem(CHUNK_RELOAD_FLAG, '1');
      } catch {
        // Private mode / blocked storage: no loop guard available, so don't risk
        // a reload cycle — surface the error instead.
        throw err;
      }
      if (alreadyRetried) throw err;

      reload();
      return new Promise(() => {}); // never resolves; the page is reloading
    });

/** React.lazy for a route component, with the recovery policy above. */
export const lazyRoute = (factory) => lazy(createChunkLoader(factory));

export default lazyRoute;
